import { ArchiveBoxIcon } from '@heroicons/react/24/outline'
import { type LoaderFunctionArgs, useOutletContext } from 'react-router'
import { href, Link } from 'react-router'
import ProductCard from '~/components/product-card'
import { OptimizedImage } from '~/components/ui/optimized-image'
import type { LayoutContext } from '~/routes/_layout'
import { getTenantContext } from '~/services/authz.server'
import { useShopContext } from '~/utils/use-shop-context'

export async function loader(args: LoaderFunctionArgs) {
  const tc = await getTenantContext(args)
  const { isB2BCustomer } = tc

  return { isB2BCustomer }
}

export default function Home() {
  const { products, styling } = useShopContext()
  const { categories, categoriesEnabled } = useOutletContext<LayoutContext>()

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col p-4 lg:p-2 lg:pt-4">
      {/* Hero Banner */}
      {styling.bannerUrl && (
        <div className="shadow-float relative mb-10 h-[340px] w-full overflow-hidden rounded-(--ws-radius) bg-(--ws-surface-2) md:h-[440px]">
          <OptimizedImage
            src={styling.bannerUrl}
            alt="Banner"
            width={1600}
            height={560}
            fit="cover"
            priority
            sizes="(min-width: 1024px) 1024px, 100vw"
            className="h-full w-full object-cover object-center"
          />
          {/* Subtle gradient overlay for better text contrast if needed */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />
        </div>
      )}

      {/* Shelf heading */}
      {products.length > 0 && (
        <div className="mb-8 flex items-baseline justify-between gap-4">
          <h2 className="text-title text-(--ws-heading)">Onze producten</h2>
          <span className="text-sm text-(--ws-text-muted) tabular-nums">
            {products.length} {products.length === 1 ? 'product' : 'producten'}
          </span>
        </div>
      )}

      {/* Products Grid */}
      <div className="grid w-full grid-cols-1 items-stretch gap-7 sm:grid-cols-2 md:gap-8 lg:grid-cols-3">
        {products.map(product => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>

      {/* Category Section */}
      {categoriesEnabled && categories.length > 0 && (
        <section className="mt-16 border-t border-(--ws-border) pt-10">
          <h2 className="text-title mb-8 text-(--ws-heading)">Categorieën</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {categories.map(category => (
              <Link
                key={category.key}
                to={href('/category/:key', { key: category.key })}
                className="ws-card-interactive p-5 text-center hover:bg-(--ws-link-tint)">
                <span className="font-semibold text-(--ws-text)">{category.label}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Empty state if no products */}
      {products.length === 0 && (
        <div className="ws-tile-empty">
          <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-(--ws-accent-tint) text-(--ws-accent)">
            <ArchiveBoxIcon className="size-8" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-medium text-(--ws-text)">Geen producten beschikbaar</p>
          <p className="mt-1 text-sm text-(--ws-text-muted)">Kom later terug voor nieuwe producten.</p>
        </div>
      )}
    </div>
  )
}
